import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import { saveAs } from 'file-saver';
import '../../../Assets/font/fonts.css';
// eslint-disable-next-line max-len
import {
  CertificateV2,
  LanguageComponent,
} from '@sdir/httpclient/lib/clients/aps/personalcertificate';
import { useApi } from '@sdir/httpclient/lib/hooks/useApi';
import { Certificate as CertificateCard, ErrorWithTrace } from '@sdir/blueprint.aps/lib/components';
import { CertificateProps } from '@sdir/blueprint.aps/lib/components/Organisms/Certificate';
import { Loader, SdirButton } from '@sdir/sds';
import { personalCertificateApi } from '../../../httpclient';
import { useCertificateCodes } from '../../../services/hooks';
import { CapitalizeString } from '../../../helpers/Certificates';
import { DEFAULT_LANG } from '../../../helpers/languageHelpers';

type CertificateModalContentProps = {
  certificate: any;
};

const CertificateModalContent: React.FC<CertificateModalContentProps> = ({ certificate }) => {
  const intl = useIntl();
  const [currentCertificate, setCurrentCertificate] = useState<CertificateV2>();
  const [certificateLanguage, setCertificateLanguage] = useState<string>();
  const certificateUid = certificate?.uid;

  const {
    result,
    loading,
    errorObject: error,
    callApi,
  } = useApi({
    apiFunction: (uid, options = {}) =>
      personalCertificateApi.v2PersonalcertificateGetpersonalcertificatev2UidGet(
        uid || certificateUid || '',
        options
      ),
    callApiOnInit: true,
  });

  const { loading: loadingPdf, callApi: generatePdf } = useApi({
    apiFunction: (uid) =>
      personalCertificateApi.v1PersonalcertificateGeneratepersonalcertificatepdfAddWatermarkGet(
        uid,
        true,
        {
          headers: { 'Accept-Language': certificateLanguage || intl?.locale || DEFAULT_LANG },
          responseType: 'blob',
        }
      ),
    callApiOnInit: false,
    successCallback: (response: any) => {
      const blob = new Blob([response], { type: 'application/octet-stream' });
      const certNr = result?.certificateNumber;
      const fileName = `Certificate${certNr ? `_${certNr}` : ''}.pdf`;
      saveAs(blob, fileName);
    },
  });

  const codes = useCertificateCodes();

  useEffect(() => {
    if (codes && codes.result && result && result.certificateType) {
      const cert = codes.result.find(
        (certificates) => certificates.code === result.certificateType
      );
      if (cert) setCurrentCertificate(cert);
    }
  }, [codes, result]);

  const getLocalizedString = useCallback(
    (langObj: { [key: string]: string } | LanguageComponent | null | undefined) => {
      if (langObj) {
        const langKey = intl.locale || DEFAULT_LANG;
        return langObj[langKey] || langObj.no || '';
      }
      return '';
    },
    [intl.locale]
  );

  const Remap = useCallback(
    (list) => {
      const resultList: Array<string> = new Array<string>();
      list?.forEach((item) => {
        if (item !== undefined) {
          resultList.push(getLocalizedString(item) || '');
        }
      });
      return resultList;
    },
    [getLocalizedString]
  );

  const onChangeLanguage = (newLang: string) => {
    const headerLang = newLang === 'no' ? DEFAULT_LANG : newLang;
    setCertificateLanguage(headerLang);
    callApi(certificateUid || '', { headers: { 'Accept-Language': headerLang } });
  };

  const onDownloadPdf = () => {
    generatePdf(certificateUid);
  };

  const certificateCard = useMemo((): CertificateProps | null => {
    if (!result) return null;
    const positionList =
      result?.limitations?.map((l) => {
        return {
          position: l.capacity || '',
          limitations: l.limit ? [CapitalizeString(l.limit)] : [],
        };
      }) || [];
    return {
      name: `${result?.firstName} ${result?.middleName || ''} ${result?.lastName}` || '',
      authorizedOfficial: result?.authorizedOfficial || '',
      issuingAuthority: result?.issuingAuthority,
      dateOfBirth: result?.dateOfBirth || '',
      certificateNr:
        !result?.certificateNumber || result?.certificateNumber === '0'
          ? '-'
          : result?.certificateNumber,
      certificateName: CapitalizeString(result?.certificateName || ''),
      issuedDate: result?.issuedDate,
      validFrom: result?.validFromDate,
      minimumRequirements: result?.minimumRequirements || '',
      accessibilityStatement: result?.accessibilityRequirement || '',
      limitationStatement: result?.capacityLimitationStatement || '',
      meetsRequirementStatement: result?.meetsRequirementUntil || '',
      positionList,
      hideLanguageSelector: false,
      qrData: `${process.env.REACT_APP_VERIFY_URL}/certificate/${certificateUid}`,
      qrImageLoading: false,
      onChangeLanguage,
    };
  }, [Remap, getLocalizedString, result, currentCertificate, certificateUid]);

  return (
    <Wrapper>
      {loading && <Loader />}
      {error && <ErrorWithTrace error={error} trace={error?.response?.headers?.traceresponse} />}
      {!loading && certificateUid && (
        <ActionWrapper>
          <SdirButton
            onClick={onDownloadPdf}
            text={intl.formatMessage({ id: 'general.button.downloadpdf' })}
            disabled={loadingPdf}
          />
          {loadingPdf && <Loader size="30px" />}
        </ActionWrapper>
      )}
      {certificateCard && (
        <CertificateWrapper translate="no">
          <CertificateCard {...certificateCard} />
        </CertificateWrapper>
      )}
    </Wrapper>
  );
};
export default CertificateModalContent;

const Wrapper = styled.div`
  max-height: 70vh;
  overflow: auto;
  padding: 0 10px 0 5px;
`;

const CertificateWrapper = styled.div`
  margin-bottom: 20px;
  p {
    margin: 0;
  }
`;

const ActionWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  button {
    margin-right: 20px;
  }
`;
